import GenericTable from "components/table";
import { ApiKey, apiKeysApi } from "api/models";
import React, { useState } from "react";
import { Trash } from "lucide-react";
import { useConfirmation } from "components/dialog/confirmation";
import { useAuthContext } from "auth/AuthProvider";
import { handleAxiosAndCaughtError } from "utils";
import { CellContext } from "@tanstack/react-table";
import { DATE_TIME_FORMAT } from "constants/date";
import dayjs from "dayjs";
import AddApiKeyForm from "./AddApiKeyForm";
import EditApiKeyForm from "./EditApiKeyForm";
import { Dialog } from "components/dialog";
import { useTranslation } from "react-i18next";

type ApiKeyTableProps = {
  tableData: ApiKey[];
  fetchApiKeys: () => Promise<void>;
  workspaceId?: string;
};

const ApiKeysDataTable: React.FC<ApiKeyTableProps> = ({
  tableData,
  fetchApiKeys,
  workspaceId,
}) => {
  const { t } = useTranslation();
  const [deletingRowId, setDeletingRowId] = React.useState<string | null>(null);
  const { hasPermission } = useAuthContext();

  const { confirm } = useConfirmation();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);

  const handleDeleteApiKey = async (apiKeyId: string) => {
    const confirmed = await confirm({
      title: t("apiKeys.deleteApiKey"),
      message: t("apiKeys.deleteApiKeyConfirm"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      setDeletingRowId(apiKeyId);
      try {
        await apiKeysApi.delete(workspaceId, apiKeyId);
        await fetchApiKeys();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setDeletingRowId(null);
      }
    }
  };

  const handleEditApiKey = (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditDialogOpen(false);
    setSelectedApiKey(null);
  };

  const columns = [
    {
      header: t("common.name"),
      accessorKey: "name",
      cell: ({ row }: CellContext<ApiKey, any>) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {row.original.name || t("apiKeys.unnamedApiKey")}
        </span>
      ),
    },
    {
      header: t("library.createdDate"),
      accessorKey: "created_at",
      cell: ({ row }: CellContext<ApiKey, any>) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {row.original.created_at
            ? dayjs(row.original.created_at).format(DATE_TIME_FORMAT)
            : t("apiKeys.notAvailable")}
        </span>
      ),
    },
    {
      header: t("library.expiresAt"),
      accessorKey: "expire_date",
      cell: ({ row }: CellContext<ApiKey, any>) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {row.original.expire_date
            ? dayjs(row.original.expire_date).format(DATE_TIME_FORMAT)
            : t("apiKeys.never")}
        </span>
      ),
    },
    {
      header: t("library.status"),
      accessorKey: "status",
      cell: ({ row }: CellContext<ApiKey, any>) => {
        const isExpired = row.original.expire_date
          ? dayjs().isAfter(dayjs(row.original.expire_date))
          : false;

        if (isExpired || row.original.status === "inactive") {
          return (
            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-300">
              {row.original.status === "inactive" ? t("apiKeys.statusInactive") : t("apiKeys.statusExpired")}
            </span>
          );
        } else {
          return (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
              {t("apiKeys.statusActive")}
            </span>
          );
        }
      },
    },
    {
      header: t("library.domains"),
      accessorKey: "domains",
      cell: ({ row }: CellContext<ApiKey, any>) => (
        <div className="flex flex-wrap gap-1">
          {row.original.domains?.length > 0
            ? row.original.domains.map((domain, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                >
                  {domain}
                </span>
              ))
            : null}
        </div>
      ),
    },
    {
      header: t("library.ipAddresses"),
      accessorKey: "ip_addresses",
      cell: ({ row }: CellContext<ApiKey, any>) => (
        <div className="flex flex-wrap gap-1">
          {row.original.ip_addresses?.length > 0
            ? row.original.ip_addresses.map((ip, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                >
                  {ip}
                </span>
              ))
            : null}
        </div>
      ),
    },
    {
      header: t("apiKeys.permissions"),
      accessorKey: "permissions",
      cell: ({ row }: CellContext<ApiKey, any>) => (
        <div className="flex flex-wrap gap-1">
          {row.original.permissions?.map((permission, index) => (
            <span
              key={index}
              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 dark:bg-gray-900 dark:text-gray-300"
            >
              {permission}
            </span>
          )) || <span className="text-sm text-gray-400">{t("apiKeys.noPermissions")}</span>}
        </div>
      ),
    },
    {
      header: t("library.actions"),
      accessorKey: "actions",
      cell: ({ row }: CellContext<ApiKey, any>) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteApiKey(row.original.id);
            }}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            title={t("apiKeys.deleteApiKeyTitle")}
          >
            <Trash className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <GenericTable
        data={tableData}
        columns={columns}
        onAdd={() => setIsAddDialogOpen(true)}
        onRowClick={(item) => {
          handleEditApiKey(item);
        }}
        addLabel={t("apiKeys.addApiKey")}
      />

      {isAddDialogOpen && (
        <Dialog
          isOpen={isAddDialogOpen}
          setIsOpen={() => setIsAddDialogOpen(false)}
        >
          <AddApiKeyForm
            onClose={() => setIsAddDialogOpen(false)}
            onSuccess={fetchApiKeys}
            workspaceId={workspaceId}
          />
        </Dialog>
      )}

      {isEditDialogOpen && selectedApiKey && (
        <Dialog isOpen={isEditDialogOpen} setIsOpen={handleCloseEditModal}>
          <EditApiKeyForm
            onClose={handleCloseEditModal}
            onSuccess={fetchApiKeys}
            apiKey={selectedApiKey}
            workspaceId={workspaceId}
          />
        </Dialog>
      )}
    </div>
  );
};

export default ApiKeysDataTable;
